// src/services/api/projects.service.ts

import { apiClient, makeApiRequest } from './client';
import { endpoints } from './config';
import { 
  Project, 
  CreateProjectRequest, 
  UpdateProjectRequest,
  AssignEmployeeRequest,
  Task
} from './types';

export class ProjectsService {
  async getProjects(): Promise<Project[]> {
    return makeApiRequest(() => 
      apiClient.get<Project[]>(endpoints.projects.list)
    );
  }

  async getProjectById(id: number): Promise<Project> {
    return makeApiRequest(() => 
      apiClient.get<Project>(endpoints.projects.byId(id))
    );
  }

  async createProject(projectData: CreateProjectRequest): Promise<Project> {
    return makeApiRequest(() => 
      apiClient.post<Project>(endpoints.projects.create, projectData)
    );
  }

  async updateProject(id: number, updates: UpdateProjectRequest): Promise<Project> {
    return makeApiRequest(() => 
      apiClient.put<Project>(endpoints.projects.update(id), updates)
    );
  }

  async completeProject(id: number): Promise<{ message: string; project: Partial<Project> }> {
    return makeApiRequest(() => 
      apiClient.put<{ message: string; project: Partial<Project> }>(endpoints.projects.complete(id))
    );
  }

  async deleteProject(id: number): Promise<void> {
    return makeApiRequest(() => 
      apiClient.delete(endpoints.projects.delete(id))
    );
  }

  async assignEmployeeToProject(projectId: number, employeeId: number): Promise<Project> {
    return makeApiRequest(() => 
      apiClient.post<Project>(
        endpoints.projects.assign(projectId, employeeId), 
        { employee_id: employeeId }
      )
    );
  }

  async getProjectTasks(projectId: number): Promise<Task[]> {
    return makeApiRequest(() => 
      apiClient.get<Task[]>(endpoints.projects.tasks, {
        project_id: projectId
      })
    );
  }

  // Helper method to get projects for a specific employee (used in employee dashboard)
  async getProjectsByEmployee(employeeId: number): Promise<Project[]> {
    return makeApiRequest(() => 
      apiClient.get<Project[]>(endpoints.projects.list, { employee_id: employeeId })
    );
  }
}

export const projectsService = new ProjectsService();
